import { ArrowDownLeft, ArrowUp, Bell } from '@phosphor-icons/react';
import { Button, Toggle, VText } from '@vake/ui-kit';
import { FC, useState } from 'react';
import {
    useAOINorInsight,
    useDarkVesselInsightInNorway,
    useGlobalSanctionedInsight,
    useNorwayTransshipmentInsight,
    useRelevantSubscriptions,
    useSanctionedVesselInsightInNorway,
} from '../requests/insightDashboard';
import { DetectionImage } from '../shared/components/Popup';
import { VakeClipboard } from '../utils/clipboard';
import { apiBaseUrl } from '../utils/env';
import { InsightDashboard } from '../utils/interfaces';
import { SendToMattermost } from './Report/components/send-to-mattermost';

const RelevantSubscriptions: FC<{ insight_uuid: string; date: string; lat: number; lon: number }> = ({
    insight_uuid,
    date,
    lat,
    lon,
}) => {
    const { data: relevantSubscriptions, isLoading } = useRelevantSubscriptions(insight_uuid);

    if (isLoading) {
        return <div>Finding relevant subscriptions</div>;
    }
    if (relevantSubscriptions && relevantSubscriptions.length === 0) {
        return <>No subcriptions found</>;
    }
    return (
        <>
            {relevantSubscriptions
                ? relevantSubscriptions.map((item, index) => {
                      return (
                          <a
                              style={{ color: 'black', background: '#aaa' }}
                              key={index}
                              href={'/overwatch/' + item.uuid + `?date=${date}&lat_lon=${lat},${lon}&uuid=${item.uuid}`}
                              target="_blank"
                          >
                              {item.description}
                          </a>
                      );
                  })
                : null}
        </>
    );
};

const InformationHolder: FC<{ infoType: string; infoValue: string | null }> = ({ infoType, infoValue }) => {
    if (!infoValue) {
        return null;
    }
    return (
        <div className="flex justify-between p-2">
            <VText className="text-label-quaternary">{infoType + ': '}</VText>
            <VText className="text-label-quaternary">{infoValue}</VText>
            <VakeClipboard infoValue={infoValue} />
        </div>
    );
};

const InsightCard: FC<{ insight: InsightDashboard }> = ({ insight }) => {
    const [findSubscriptions, toggleFindSubscriptions] = useState<boolean>(false);

    const dateAndTime = insight.timestamp.toString().split('T');
    const ts_text = dateAndTime[0] + ' ' + dateAndTime[1].slice(0, 8);
    const lat_lon_text = '(' + insight.lat.toString() + ',' + insight.lon.toString() + ')';
    const tags_text = insight.tags && insight.tags.length > 0 ? insight.tags.join(' ') : 'no tags';
    return (
        <div className="bg-primary-action-background-2 w-72">
            <div className="overflow-hidden">
                {insight.display_img_path && (
                    <DetectionImage image_path={apiBaseUrl() + '/insights/image/uuid/' + insight.insight_uuid} />
                )}
                {!insight.display_img_path && (
                    <div>
                        <div className="flex justify-between p-2">
                            <VText significance="quaternary">No image available</VText>
                            <SendToMattermost
                                textOnButton={'Notify Vake'}
                                iconOnButton={<Bell size={16} />}
                                textToSend={`No image exists for insight: ${insight.insight_uuid} on [page](${window.location.href})`}
                                successMessage={'Thank you for notifying!'}
                            />
                        </div>
                    </div>
                )}
            </div>
            <div>
                <InformationHolder infoType="uuid" infoValue={insight.insight_uuid} />
                <InformationHolder infoType="timestamp" infoValue={ts_text} />
                <InformationHolder infoType="mmsi" infoValue={insight.mmsi ? insight.mmsi.toString() : null} />
                <InformationHolder infoType="lon_lat" infoValue={lat_lon_text} />
                <InformationHolder infoType="tags" infoValue={tags_text} />

                {findSubscriptions ? (
                    <RelevantSubscriptions
                        insight_uuid={insight.insight_uuid}
                        date={dateAndTime[0]}
                        lat={insight.lat}
                        lon={insight.lon}
                    />
                ) : (
                    <Button
                        variant="neutral"
                        onClick={() => toggleFindSubscriptions(true)}
                        text="Find relevant Overwatches"
                    />
                )}
            </div>
        </div>
    );
};

export const InsightHolders: FC<{ insights: InsightDashboard[] }> = ({ insights }) => {
    if (insights.length === 0) {
        return <VText significance="secondary">No data available</VText>;
    }
    return (
        <div className="flex overflow-x-scroll">
            {insights.map((item, idx) => {
                return <InsightCard insight={item} key={idx} />;
            })}
        </div>
    );
};

enum Countries {
    norway = 'norway',
    netherlands = 'netherlands',
}

const RecentInsightDashboard: FC = () => {
    const specific_aoi_days_period = '10';
    const [lastNDays, setLastNDays] = useState<string>('2');
    const [country, setLCountry] = useState<Countries>(Countries.norway);
    const { data: norwayTransshipmentInsights } = useNorwayTransshipmentInsight(lastNDays, country);
    const { data: confidentDarkVesselNor } = useDarkVesselInsightInNorway(lastNDays, country);
    const { data: sanctionedVesselNor } = useSanctionedVesselInsightInNorway(lastNDays, country);
    const { data: SanctionedInsightsOutsideNorway } = useGlobalSanctionedInsight(lastNDays, country);
    const { data: NuclearInsights } = useAOINorInsight(specific_aoi_days_period, country, 'nuclear');
    const { data: LNG2Insights } = useAOINorInsight(specific_aoi_days_period, country, 'LNG2');

    const handleNDaysinput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (/^\d*\.?\d*$/.test(value)) {
            setLastNDays(value);
        }
    };
    const handleCountryToggle = () => {
        switch (country) {
            case Countries.norway:
                setLCountry(Countries.netherlands);
                break;
            case Countries.netherlands:
                setLCountry(Countries.norway);
                break;
        }
    };
    return (
        <div className="pt-16">
            <div className="bg-grey-700 w-80 rounded p-4">
                <Toggle
                    onIcon={<ArrowUp size={32} />}
                    offIcon={<ArrowDownLeft size={32} />}
                    onChange={handleCountryToggle}
                    isOn={country === Countries.norway}
                />
                <div className="flex mt-4 mb-4 justify-around">
                    <VText variant="B1">Number of 24 hours to gather data </VText>
                    <input
                        type="text"
                        className="text-system-neutral-grey-0 w-8"
                        value={lastNDays}
                        onChange={handleNDaysinput}
                    />
                </div>
            </div>
            <>
                <div style={{ height: '4vw' }} />
                <VText variant="H2">
                    Containing {norwayTransshipmentInsights ? norwayTransshipmentInsights.length : ''} transshipment in{' '}
                    {country}.
                </VText>
                {norwayTransshipmentInsights ? (
                    norwayTransshipmentInsights.length > 0 ? (
                        <InsightHolders insights={norwayTransshipmentInsights} />
                    ) : (
                        <div>No images found</div>
                    )
                ) : null}
                <div style={{ height: '4vw' }} />
                <VText variant="H2">
                    Containing {confidentDarkVesselNor ? confidentDarkVesselNor.length : ''} dark detections with above
                    90 % confidence in {country} zone.
                </VText>
                {confidentDarkVesselNor ? (
                    confidentDarkVesselNor.length > 0 ? (
                        <InsightHolders insights={confidentDarkVesselNor} />
                    ) : (
                        <div>No images found</div>
                    )
                ) : null}
                <div style={{ height: '4vw' }} />
                <VText variant="H2">
                    Containing {sanctionedVesselNor ? sanctionedVesselNor.length : ''} images of sanctioned vessels
                    within {country} zone.
                </VText>
                {sanctionedVesselNor ? (
                    sanctionedVesselNor.length > 0 ? (
                        <InsightHolders insights={sanctionedVesselNor} />
                    ) : (
                        <div>No images found</div>
                    )
                ) : null}
                <div style={{ height: '6vw' }} />
                <VText variant="H2">
                    Containing {SanctionedInsightsOutsideNorway ? SanctionedInsightsOutsideNorway.length : ''} images of
                    sanctioned mmsis outside {country}
                </VText>
                {SanctionedInsightsOutsideNorway ? (
                    SanctionedInsightsOutsideNorway.length > 0 ? (
                        <InsightHolders insights={SanctionedInsightsOutsideNorway} />
                    ) : (
                        <div>No images found</div>
                    )
                ) : null}

                <div style={{ height: '6vw' }} />
                {country === 'norway' && (
                    <>
                        <VText variant="H2">
                            Containing {NuclearInsights ? NuclearInsights.length : ''} insights in stråligsvernområder
                            in the last {specific_aoi_days_period} days. (273381660 is AKADEMIK LOMONOSOV and expected
                            to be there.)
                        </VText>
                        {NuclearInsights ? (
                            NuclearInsights.length > 0 ? (
                                <InsightHolders insights={NuclearInsights} />
                            ) : (
                                <div>No images found</div>
                            )
                        ) : null}
                        <div style={{ height: '4vw' }} />
                        <VText variant="H2">
                            Containing {LNG2Insights ? LNG2Insights.length : ''} insights close to the harbour of LNG2
                            in the last {specific_aoi_days_period} days. Usual Mmsis: (273212170, 273458860, 273394220,
                            273389730, 273298030, 273218240)
                        </VText>
                        {LNG2Insights ? (
                            LNG2Insights.length > 0 ? (
                                <InsightHolders insights={LNG2Insights} />
                            ) : (
                                <div>No images found</div>
                            )
                        ) : null}
                    </>
                )}
            </>
        </div>
    );
};

export default RecentInsightDashboard;
